import { getCheckoutAoeQuestions } from "../labs/services/aoeNormalizer";

const clean = (value) => String(value || "").trim();

// Runtime assignment IDs are tenant-specific and may be absent in Custom
// Program previews. The authored requirement ID remains stable across both
// Program and Custom Program projections, so it is the safe fallback for
// client-side collection state.
export const programLabDetailKey = (lab) => clean(
  lab?.assignment_id || lab?.requirement_id || lab?.release_node_id,
);

export const programLabDetail = (lab, details = {}) => (
  details?.[programLabDetailKey(lab)]
  ?? details?.[String(lab?.assignment_id)]
  ?? details?.[String(lab?.requirement_id)]
  ?? {}
);

export const checkoutLabs = (checkout) =>
  (checkout?.treatments || []).flatMap((treatment) =>
    (treatment.labs || []).map((lab) => ({
      ...lab,
      program_id: treatment.program_id,
      program_name: treatment.program_name,
    }))
  );

export const isProgramLabSelected = (selection) => Boolean(
  selection === true
  || (selection && typeof selection === "object" && selection.selected === true)
);

/**
 * Build the patient display list from the server-authored group identity.
 * This is presentation-only: validation and checkout still use the original
 * requirement rows so every Program remains traceable.
 */
export const checkoutLabGroups = (checkout) => {
  const groups = new Map();
  checkoutLabs(checkout).forEach((lab, index) => {
    const groupId = lab?.patient_lab_group_is_shared
      && lab?.patient_lab_group_id
      ? String(lab.patient_lab_group_id)
      : `individual:${programLabDetailKey(lab)}:${index}`;
    const current = groups.get(groupId);
    if (!current) {
      const programNames = Array.isArray(lab.patient_lab_group_program_names)
        ? lab.patient_lab_group_program_names.filter(Boolean)
        : [];
      groups.set(groupId, {
        ...lab,
        members: [lab],
        requirement_ids: lab?.requirement_id ? [String(lab.requirement_id)] : [],
        program_names: [...new Set([
          ...(lab?.program_name ? [lab.program_name] : []),
          ...programNames,
        ])],
      });
      return;
    }
    current.members.push(lab);
    // A shared physical Lab is mandatory when any owning Program requires it.
    // Aggregate the policy instead of allowing treatment ordering to decide
    // whether the patient sees an optional checkbox.
    current.is_required = current.is_required !== false || lab?.is_required !== false;
    if (lab?.requirement_id) current.requirement_ids.push(String(lab.requirement_id));
    if (lab?.program_name && !current.program_names.includes(lab.program_name)) {
      current.program_names.push(lab.program_name);
    }
    if (Array.isArray(lab.patient_lab_group_program_names)) {
      lab.patient_lab_group_program_names.forEach((name) => {
        if (name && !current.program_names.includes(name)) current.program_names.push(name);
      });
    }
  });
  return [...groups.values()];
};

export const selectedCheckoutLabs = (checkout, value) =>
  checkoutLabs(checkout).filter(
    (lab) => lab.is_required !== false
      || isProgramLabSelected(value?.labs?.[programLabDetailKey(lab)])
      || (
        lab?.patient_lab_group_is_shared
        && isProgramLabSelected(value?.labs?.[String(lab.patient_lab_group_id)])
      ),
  );

export const checkoutAvailabilityDecisionIds = (
  checkout,
  value,
  programIds = null,
) => {
  const allowed = programIds ? new Set((programIds || []).map(String)) : null;
  const details = value?.lab_details || {};
  return [...new Set(
    checkoutLabs(checkout)
      .filter((lab) => !allowed || allowed.has(String(lab.program_id)))
      .map((lab) => programLabDetail(lab, details))
      .map((detail) => (
        detail?.availabilityDecisionId
          || detail?.availability?.availability_decision?.decision_id
          || ""
      ))
      .map(clean)
      .filter(Boolean),
  )];
};

export const requiresProgramLabCheckout = (checkout, value) =>
  selectedCheckoutLabs(checkout, value).length > 0;

// Junction's create-order contract requires patient_address for every lab
// modality, including walk-in. Keep this list selection-aware so optional labs
// that the patient did not choose are not named in the address explanation.
export const programLabsRequiringAddress = (checkout, value) =>
  selectedCheckoutLabs(checkout, value);

export const getProgramLabAddressStateError = (checkout, value) => {
  const patientState = clean(value?.address?.state).toUpperCase();
  if (!patientState) return null;

  const labDetails = value?.lab_details || {};
  for (const lab of programLabsRequiringAddress(checkout, value)) {
    const detail = programLabDetail(lab, labDetails);
    const serviceAreaState = clean(
      detail.availability?.resolved_state || value?.availability_state,
    ).toUpperCase();
    if (serviceAreaState && serviceAreaState !== patientState) {
      return `Patient address state (${patientState}) does not match the service area ZIP code state (${serviceAreaState}). Please enter an address in ${serviceAreaState} or change your ZIP code above.`;
    }
  }

  return null;
};

export const programLabAoeQuestions = (checkout, value) =>
  selectedCheckoutLabs(checkout, value).flatMap((lab) => {
    const detail = programLabDetail(lab, value?.lab_details);
    return getCheckoutAoeQuestions(lab, selectedProgramLabMethod(lab, detail));
  });

export const hasProgramLabAoeQuestions = (checkout, value) =>
  programLabAoeQuestions(checkout, value).length > 0;

export const programLabCollectionOptions = (lab) => {
  const options = lab?.collection_options
    || lab?.collectionOptions
    || lab?.combined_methods
    || lab?.combinedMethods;
  if (Array.isArray(options) && options.length) {
    return options.map((option) => (
      typeof option === "string" ? { method: option } : option
    ));
  }
  return [{
    method: lab?.collection_method || "draw_site",
    assignment_id: lab?.assignment_id || "",
    aoe_questions: lab?.aoe_questions || [],
  }];
};

export const serviceableProgramLabCollectionOptions = (lab, detail) => {
  const authoredOptions = programLabCollectionOptions(lab);
  const serviceableMethods = detail?.availability?.is_combined
    ? detail.availability.serviceable_methods
    : null;
  if (!Array.isArray(serviceableMethods)) return authoredOptions;
  return serviceableMethods.map((candidate) => {
    const authored = authoredOptions.find((option) => (
      (candidate?.assignment_id && clean(candidate.assignment_id) === clean(option?.assignment_id))
      || (candidate?.method && clean(candidate.method) === clean(option?.method))
    ));
    return authored ? { ...candidate, ...authored } : candidate;
  });
};

export const selectedProgramLabMethod = (lab, detail) => {
  const options = programLabCollectionOptions(lab);
  const selectedAssignmentId = clean(detail?.selectedAssignmentId);
  const selectedMethod = clean(detail?.selectedMethod);
  const reportedMethods = detail?.availability?.serviceable_methods;
  const serviceable = Array.isArray(reportedMethods)
    && reportedMethods.length === 0
    && !detail?.availability?.is_combined
    ? null : reportedMethods;
  const isServiceable = (option) => (
    Array.isArray(serviceable)
      && serviceable.some((item) => (
        (item?.assignment_id && clean(item.assignment_id) === clean(option?.assignment_id))
        || (item?.method && clean(item.method) === clean(option?.method))
      ))
  );
  const selected = options.find((option) => (
    selectedAssignmentId
      && clean(option?.assignment_id) === selectedAssignmentId
  ))
    || options.find((option) => (
      selectedMethod && clean(option?.method) === selectedMethod
    ));
  if (selected && (!Array.isArray(serviceable) || isServiceable(selected))) {
    return selected;
  }
  if (Array.isArray(serviceable)) {
    const serviceableOptions = options.filter(isServiceable);
    return serviceableOptions.length === 1 ? serviceableOptions[0] : null;
  }
  return selected || options[0] || null;
};

export const selectedProgramLabAvailability = (lab, detail) => {
  const availability = detail?.availability;
  if (!availability?.is_combined) return availability;
  const selectedMethod = selectedProgramLabMethod(lab, detail);
  return availability.serviceable_methods?.find((candidate) => (
    (candidate?.assignment_id && clean(candidate.assignment_id) === clean(selectedMethod?.assignment_id))
    || (candidate?.method && clean(candidate.method) === clean(selectedMethod?.method))
  ));
};

export const programLabNeedsDrawSite = (lab, detail) => {
  const method = clean(selectedProgramLabMethod(lab, detail)?.method);
  const availability = selectedProgramLabAvailability(lab, detail);
  return ["walk_in_test", "on_site_collection"].includes(method)
    && Array.isArray(availability?.drawSites)
    && availability.drawSites.length > 0;
};

export const combinedLabHasServiceableMethod = (result) => (
  !result?.is_combined
  || (Array.isArray(result.serviceable_methods) && result.serviceable_methods.length > 0)
);

const effectiveCollectionMethod = (lab, detail) => {
  const selectedMethod = clean(selectedProgramLabMethod(lab, detail)?.method);
  if (selectedMethod) return selectedMethod;
  if (lab?.is_combined === true) return "";
  return clean(lab?.collection_method || "draw_site");
};

export const isProgramLabAddressVerified = (value) => {
  const verified = value?.address_verification;
  const address = value?.address || {};
  if (!verified?.fingerprint || !verified?.address) return false;
  return ["street", "apt", "city", "state", "zip"].every((key) => (
    clean(verified.address[key]).toUpperCase() === clean(address[key]).toUpperCase()
  ));
};

export const verifyProgramLabAddress = async (value, verifyAddress) => {
  const result = await verifyAddress(value?.address || {});
  if (!result?.isValid || !result?.verified || !result?.normalizedAddress) {
    throw new Error(
      result?.error
      || "Patient address not recognized. Please check your street address, city, state, and ZIP code.",
    );
  }
  const address = {
    street: clean(result.normalizedAddress.street),
    apt: clean(result.normalizedAddress.apt),
    city: clean(result.normalizedAddress.city),
    state: clean(result.normalizedAddress.state).toUpperCase(),
    zip: clean(result.normalizedAddress.zip),
  };
  return {
    ...value,
    address,
    address_verification: {
      fingerprint: [address.street, address.city, address.state, address.zip].join("|"),
      address,
    },
  };
};

export const emptyProgramLabCheckout = () => ({
  address: {
    street: "",
    apt: "",
    city: "",
    state: "",
    zip: "",
  },
  consents: {
    hipaa: false,
    terms: false,
    telehealth: false,
  },
  labs: {},
  lab_details: {},
});

export const getProgramLabCollectionError = (
  checkout,
  value,
  { previewOnly = false, requireVerifiedAddress = true } = {},
) => {
  if (!requiresProgramLabCheckout(checkout, value)) return null;
  const address = value?.address || {};
  if (clean(address.zip) && !/^\d{5}(?:-\d{4})?$/.test(clean(address.zip))) {
    return "Enter a valid 5-digit ZIP code.";
  }
  const labs = selectedCheckoutLabs(checkout, value);
  const labDetails = value?.lab_details || {};
  for (const lab of labs) {
    const detail = programLabDetail(lab, labDetails);
    const availability = detail.availability;
    if (availability?.source === "runtime_saved" && (
      clean(detail.selectedMethod) !== clean(availability.saved_method)
      || clean(detail.selectedAssignmentId) !== clean(availability.saved_assignment_id)
    )) {
      return "Check availability for the selected collection method before continuing.";
    }
    if (availability?.is_available === false) {
      return availability.reason || `${lab.name || "This lab"} is not available at this location.`;
    }
    if (availability?.is_available !== true) {
      return "Check availability for this ZIP code before continuing.";
    }
    // A single collection option is rendered as already selected. Treat the
    // panel's immutable collection method as the effective selection even if
    // the patient never toggles the radio.
    if (!effectiveCollectionMethod(lab, detail)) {
      return "Select an available collection method before continuing.";
    }
  }
  if (programLabsRequiringAddress(checkout, value).length > 0) {
    if (!["street", "city", "state", "zip"].every((key) => clean(address[key]))) {
      return "Please complete your patient address.";
    }
    if (clean(address.state).length !== 2) return "Enter a valid two-letter state.";
    if (!/^\d{5}(?:-\d{4})?$/.test(clean(address.zip))) {
      return "Enter a valid 5-digit ZIP code.";
    }
    const addressStateError = getProgramLabAddressStateError(checkout, value);
    if (addressStateError) return addressStateError;
    if (requireVerifiedAddress && !isProgramLabAddressVerified(value)) {
      return "Verify the patient address before continuing.";
    }
  }
  for (const lab of labs) {
    const detail = programLabDetail(lab, labDetails);
    // A single collection option is rendered as already selected. Treat the
    // panel's immutable collection method as the effective selection even if
    // the patient never toggles the radio (and therefore no change event has
    // written selectedMethod into React state).
    const method = effectiveCollectionMethod(lab, detail);
    if (!previewOnly && ["at_home_phlebotomy", "testkit"].includes(method)) {
      const addressZip = clean(value?.address?.zip).slice(0, 5);
      const checkedZip = clean(detail.collectionZip).slice(0, 5);
      const addressState = clean(value?.address?.state).toUpperCase();
      const checkedState = clean(
        detail.availability?.resolved_state || value?.availability_state,
      ).toUpperCase();
      if (
        !checkedZip
        || checkedZip !== addressZip
        || (checkedState && checkedState !== addressState)
      ) {
        const purpose = method === "testkit" ? "lab-kit delivery" : "at-home collection";
        return `Check ${purpose} availability for the verified patient address before continuing.`;
      }
    }
    if (programLabNeedsDrawSite(lab, detail) && !detail.selectedSite) {
      return "Please select a draw site.";
    }
  }
  return null;
};

export const isProgramLabCollectionComplete = (checkout, value, options = {}) =>
  !getProgramLabCollectionError(checkout, value, options);

export const isProgramLabConsentsComplete = (checkout, value) => {
  if (!requiresProgramLabCheckout(checkout, value)) return true;
  const consents = value?.consents || {};
  return consents.hipaa === true
    && consents.terms === true
    && consents.telehealth === true;
};

const aoeAnswersToDict = (answers) => {
  if (!Array.isArray(answers)) return answers && typeof answers === "object" ? answers : {};
  return answers.reduce((result, item) => {
    const markerId = item?.marker_id ?? item?.markerId;
    const questionId = item?.question_id ?? item?.questionId;
    if (markerId && questionId && String(item?.answer ?? "").trim() !== "") {
      result[`${markerId}:${questionId}`] = item.answer;
    }
    return result;
  }, {});
};

export const isProgramLabAoeComplete = (checkout, value) => {
  if (!requiresProgramLabCheckout(checkout, value)) return true;
  const labDetails = value?.lab_details || {};
  return selectedCheckoutLabs(checkout, value).every((lab) => {
    const detail = programLabDetail(lab, labDetails);
    const questions = getCheckoutAoeQuestions(
      lab,
      selectedProgramLabMethod(lab, detail),
    );
    const answers = aoeAnswersToDict(detail.aoeAnswers ?? detail.aoe_answers);
    return questions.every((q) => {
      if (!q.required) return true;
      const key = `${q.markerId}:${q.questionId}`;
      return String(answers[key] || "").trim() !== "";
    });
  });
};

export const isProgramLabCheckoutComplete = (checkout, value, options = {}) =>
  isProgramLabCollectionComplete(checkout, value, options)
  && isProgramLabConsentsComplete(checkout, value)
  && isProgramLabAoeComplete(checkout, value);

/**
 * Convert the frontend AOE answers dict (keyed by "markerId:questionId")
 * into the list format the backend normalize_aoe_answers() expects:
 *   [{ marker_id, question_id, answer }]
 *
 * The key is always stored as `${q.markerId}:${q.questionId}` by LabAoeQuestions.
 * Entries with an empty answer are omitted — the backend validates required answers.
 */
const aoeAnswersDictToList = (answers) => {
  const answersDict = aoeAnswersToDict(answers);
  return Object.entries(answersDict)
    .filter(([, answer]) => String(answer || "").trim() !== "")
    .map(([key, answer]) => {
      const separator = key.indexOf(":");
      const markerId = separator >= 0 ? key.slice(0, separator) : key;
      const questionId = separator >= 0 ? key.slice(separator + 1) : "";
      return {
        marker_id: String(markerId || ""),
        question_id: String(questionId || ""),
        answer: String(answer),
      };
    });
};

export { aoeAnswersToDict };

export const normalizeProgramLabCheckout = (value, checkout) => {
  const labDetails = value?.lab_details || value?.labs || {};
  const normalizedLabs = {};
  const selectedLabs = checkout
    ? selectedCheckoutLabs(checkout, value)
    : [];
  const consumedDetailKeys = new Set();
  selectedLabs.forEach((lab) => {
    const detailKey = programLabDetailKey(lab);
    consumedDetailKeys.add(detailKey);
    consumedDetailKeys.add(String(lab?.assignment_id));
    const rawDetail = programLabDetail(lab, labDetails);
    const detail = rawDetail && typeof rawDetail === "object" ? rawDetail : {};
    const selectedMethod = selectedProgramLabMethod(lab, detail);
    normalizedLabs[detailKey] = {
      selected: true,
      selectedMethod: effectiveCollectionMethod(lab, detail) || undefined,
      selectedAssignmentId: selectedMethod?.assignment_id || undefined,
      selectedSite: detail.selectedSite || undefined,
      collectionZip: clean(detail.collectionZip) || undefined,
      availabilityFingerprint: detail.availability?.policy_fingerprint || undefined,
      availabilityDecisionId: clean(
        detail.availabilityDecisionId
          || detail.availability?.availability_decision?.decision_id,
      ) || undefined,
      // Convert dict → list so the backend's normalize_aoe_answers() receives
      // the expected [{marker_id, question_id, answer}] shape.
      aoeAnswers: aoeAnswersDictToList(detail.aoeAnswers ?? detail.aoe_answers),
    };
  });
  Object.entries(labDetails).forEach(([detailKey, detail]) => {
    if (consumedDetailKeys.has(detailKey)) return;
    normalizedLabs[detailKey] = {
      selectedMethod: effectiveCollectionMethod(null, detail) || undefined,
      selectedAssignmentId: undefined,
      selectedSite: detail.selectedSite || undefined,
      collectionZip: clean(detail.collectionZip) || undefined,
      availabilityFingerprint: detail.availability?.policy_fingerprint || undefined,
      availabilityDecisionId: clean(
        detail.availabilityDecisionId
          || detail.availability?.availability_decision?.decision_id,
      ) || undefined,
      aoeAnswers: aoeAnswersDictToList(detail.aoeAnswers ?? detail.aoe_answers),
    };
  });
  return {
    address: {
      street: clean(value?.address?.street),
      apt: clean(value?.address?.apt),
      city: clean(value?.address?.city),
      state: clean(value?.address?.state).toUpperCase(),
      zip: clean(value?.address?.zip),
    },
    consents: {
      hipaa: value?.consents?.hipaa === true,
      terms: value?.consents?.terms === true,
      telehealth: value?.consents?.telehealth === true,
    },
    labs: normalizedLabs,
  };
};
